/**
 * Perda de sinal: quando o silêncio começou e como dizê-lo — GUPM-303. TS puro.
 *
 * Separado do `nivel-exibido.ts` porque depende da cadência de publicação, que mora na
 * camada da API GPX (`gpx-monitoramento.ts`). O `nivel-exibido` responde "que cor";
 * este responde "desde quando e por quanto tempo".
 */
import { cadenciaEsperadaMin, janelaFrescorMin } from './gpx-monitoramento.ts';

/**
 * Enum do banco → tipo da API GPX. Existe por UMA divergência que já esconde defeito:
 * o banco chama tiltímetro de **`TILT`** e a API de **`TIL`**.
 *
 * Toda tabela indexada por tipo neste domínio (cadência, janela de frescor) usa a chave
 * da API. Perguntar com o enum do banco cai no default silenciosamente — na primeira
 * query da investigação do GUPM-303 foi exatamente isso: o tiltímetro foi para o default
 * de 24h e o TIL-05, que estava 9,5h mudo, **sumiu do relatório**.
 */
const TIPO_API_POR_ENUM: Record<string, string> = { TILT: 'TIL' };

/** Tipo da API GPX correspondente ao enum do banco (`TILT` → `TIL`). */
export function tipoApiDeInstrumento(typeDoBanco: string): string {
  return TIPO_API_POR_ENUM[typeDoBanco] ?? typeDoBanco;
}

/**
 * Instante em que o silêncio começou: a primeira publicação ESPERADA que não veio.
 *
 * Não é a última leitura recebida. Às 04:00 o instrumento publicou e estava tudo certo —
 * o que houve de errado foi a ausência das 04:30. Contar de 04:00 infla o tempo de
 * silêncio por uma cadência inteira e datar o início em 04:00 afirma falha num instante
 * em que o dado chegou.
 */
export function inicioDoSilencio(ultimaLeitura: Date, typeDoBanco: string): Date {
  const cadenciaMs = cadenciaEsperadaMin(tipoApiDeInstrumento(typeDoBanco)) * 60_000;
  return new Date(ultimaLeitura.getTime() + cadenciaMs);
}

/**
 * A leitura está fora da janela de frescor do tipo? — GUPM-306.
 *
 * "Congelada" = o instrumento já deveria ter publicado de novo e não publicou, então esse
 * número não é medição atual. É a mesma régua que a ingestão usa para derivar `is_online`
 * (`janelaFrescorMin`), mas derivada aqui do próprio instante da leitura, e não lida do
 * banco: assim a resposta não depende de a ingestão ter avaliado o instrumento na última
 * rodada — quem sai da janela de requisição da v2 para de ser avaliado e **congela o
 * `is_online` que tinha** (o INA-12A parou de ser avaliado em 22/08).
 *
 * Mora neste módulo, e não em `anomalias.ts`, porque a tradução `TILT → TIL` e a tabela de
 * cadência vivem aqui — perguntar com o enum do banco cai no default de 12h45 calado.
 */
export function leituraForaDaJanela(leitura: Date, typeDoBanco: string, agora: Date): boolean {
  const janelaMs = janelaFrescorMin(tipoApiDeInstrumento(typeDoBanco)) * 60_000;
  return agora.getTime() - leitura.getTime() > janelaMs;
}

const MIN_MS = 60_000;
const HORA_MIN = 60;
/**
 * A partir daqui o total vira dias — com as horas restantes, e sem o minuto (GUPM-309):
 * "52h 10min" não é legível num plantão, mas "2 dias" para 52h subestimava a falha.
 */
const LIMITE_HORAS_LEGIVEIS = 48;

/**
 * Tempo de silêncio em texto — `"36min"`, `"7h 36min"`, `"2h"`, `"2 dias 17h"`, `"3 dias"`.
 *
 * Relógio atrás do início (deriva de clock, ou episódio que ainda não venceu a primeira
 * publicação) devolve `"0min"` em vez de tempo negativo: um "-12min" na tela é lido como
 * bug da tela, não como o que é.
 */
export function tempoSemSinal(desde: Date, agora: Date): string {
  const totalMin = Math.floor((agora.getTime() - desde.getTime()) / MIN_MS);
  if (!Number.isFinite(totalMin) || totalMin <= 0) return '0min';

  const horas = Math.floor(totalMin / HORA_MIN);
  if (horas >= LIMITE_HORAS_LEGIVEIS) {
    const dias = Math.floor(horas / 24);
    // GUPM-309 — as horas restantes ENTRAM no texto. Descartá-las errava sempre para
    // baixo, em até 23h: o TIL-05, mudo há 65,4h, era anunciado como "2 dias", e quem
    // lê "2 dias" entende 48h. Num sensor de ruptura, subestimar é o lado errado do erro.
    // O minuto continua fora — nesta faixa não muda decisão e devolveria o "52h 10min"
    // que o LIMITE_HORAS_LEGIVEIS existe para evitar.
    const restoH = horas % 24;
    const rotulo = `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
    return restoH === 0 ? rotulo : `${rotulo} ${restoH}h`;
  }
  const min = totalMin % HORA_MIN;
  if (horas === 0) return `${min}min`;
  // Hora cheia não imprime "0min" — "2h 0min" parece dado truncado.
  return min === 0 ? `${horas}h` : `${horas}h ${min}min`;
}
